import Phaser from "phaser";
import { TEX } from "@/data/assetManifest";
import { T, TH } from "@/data/tileIndex";
import { TilemapRenderer } from "@/systems/TilemapRenderer";
import { FIELD_W, FIELD_H, FIELD_PIX_W, FIELD_PIX_H, TILE, type FieldTheme } from "./FieldConstants";

// Real-asset field tilemap renderer per theme.
export function buildFieldTilemap(scene: Phaser.Scene, theme: FieldTheme) {
  const r = new TilemapRenderer(scene, FIELD_W, FIELD_H, TILE);
  const rng = (() => {
    let s = theme.charCodeAt(0) * 7919;
    return () => {
      s = (s + 0x6d2b79f5) >>> 0;
      let t = s;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  })();

  if (theme === "lorencia_outskirt") {
    // Newbie outskirt: green grassland with dirt patches, scattered bushes,
    // a few trees and broken carts/pots — home of Spider/Bandit/Knight Apprentice/Rat.
    r.fill(T.grassGreen);
    for (let i = 0; i < 12; i++) {
      const cx = Math.floor(rng() * FIELD_W);
      const cy = Math.floor(rng() * FIELD_H);
      const sz = 1 + Math.floor(rng() * 3);
      r.rect(cx - sz, cy - sz, cx + sz, cy + sz, T.pathDirt);
    }
    // dirt path leading north (back to Lorencia town)
    const px0 = Math.floor(FIELD_W / 2);
    for (let y = 0; y < FIELD_H; y++) {
      r.rect(px0 - 1, y, px0 + 1, y, T.pathDirt);
    }
    for (let i = 0; i < 160; i++) {
      const x = Math.floor(rng() * FIELD_W);
      const y = Math.floor(rng() * FIELD_H);
      r.setDeco(
        x,
        y,
        rng() < 0.5 ? T.bushGreen : rng() < 0.8 ? T.fernGreen : T.grassFlowers,
      );
    }
    r.render();
    for (let i = 0; i < 14; i++) {
      const x = Math.floor(rng() * (FIELD_W - 2));
      const y = Math.floor(rng() * (FIELD_H - 2));
      scene.add
        .image(x * TILE + TILE, y * TILE + TILE, TEX.tinyTiles, T.treeBig)
        .setScale((TILE / 16) * 2)
        .setDepth((y + 2) * TILE);
    }
    for (let i = 0; i < 8; i++) {
      const x = Math.floor(rng() * FIELD_W);
      const y = Math.floor(rng() * FIELD_H);
      scene.add
        .image(
          x * TILE,
          y * TILE,
          TEX.tinyThings,
          rng() < 0.5 ? TH.chestS : TH.potM,
        )
        .setScale(TILE / 16)
        .setDepth(y * TILE);
    }
  } else if (theme === "noria") {
    // Noria: use AI-generated base map + layered props when available.
    const useBaseMap = scene.textures.exists(TEX.noriaBase);
    if (useBaseMap) {
      scene.add
        .image(FIELD_PIX_W / 2, FIELD_PIX_H / 2, TEX.noriaBase)
        .setDisplaySize(FIELD_PIX_W, FIELD_PIX_H)
        .setDepth(0);
    } else {
      // Procedural fallback: grass + ferns + trees + dirt clearings
      r.fill(T.grassGreen);
      for (let i = 0; i < 8; i++) {
        const cx = Math.floor(rng() * FIELD_W);
        const cy = Math.floor(rng() * FIELD_H);
        const sz = 2 + Math.floor(rng() * 3);
        r.rect(cx - sz, cy - sz, cx + sz, cy + sz, T.pathDirt);
      }
      let px = 4;
      for (let y = 4; y < FIELD_H - 4; y += 2) {
        px = Phaser.Math.Clamp(px + Math.floor(rng() * 5) - 2, 4, FIELD_W - 4);
        r.rect(px - 1, y, px + 1, y, T.pathDirt);
      }
      for (let i = 0; i < 200; i++) {
        const x = Math.floor(rng() * FIELD_W);
        const y = Math.floor(rng() * FIELD_H);
        r.setDeco(
          x, y,
          rng() < 0.4 ? T.bushGreen : rng() < 0.7 ? T.fernGreen : T.grassFlowers,
        );
      }
      r.render();
      for (let i = 0; i < 30; i++) {
        const x = Math.floor(rng() * (FIELD_W - 2));
        const y = Math.floor(rng() * (FIELD_H - 2));
        scene.add
          .image(x * TILE + TILE, y * TILE + TILE, TEX.tinyTiles, T.treeBig)
          .setScale((TILE / 16) * 2)
          .setDepth((y + 2) * TILE);
      }
    }

    // Layered props (always placed, whether base map or procedural)
    const NORIA_PROPS: Array<{
      key: string;
      x: number; y: number;
      w: number; h: number;
    }> = [
      // Ancient oaks — perimeter of map
      { key: TEX.propAncientOak, x: 200, y: 1100, w: 230, h: 208 },
      { key: TEX.propAncientOak, x: 500, y: 1050, w: 230, h: 208 },
      { key: TEX.propAncientOak, x: 1400, y: 1080, w: 230, h: 208 },
      { key: TEX.propAncientOak, x: 1700, y: 1120, w: 230, h: 208 },
      { key: TEX.propAncientOak, x: 300, y: 800, w: 230, h: 208 },
      { key: TEX.propAncientOak, x: 1600, y: 820, w: 230, h: 208 },
      { key: TEX.propAncientOak, x: 150, y: 550, w: 230, h: 208 },
      { key: TEX.propAncientOak, x: 1750, y: 520, w: 230, h: 208 },
      { key: TEX.propAncientOak, x: 400, y: 350, w: 230, h: 208 },
      { key: TEX.propAncientOak, x: 1500, y: 380, w: 230, h: 208 },
      { key: TEX.propAncientOak, x: 250, y: 200, w: 230, h: 208 },
      { key: TEX.propAncientOak, x: 1650, y: 180, w: 230, h: 208 },
      // Glowing mushrooms
      { key: TEX.propGlowingMushroom, x: 600, y: 1150, w: 100, h: 100 },
      { key: TEX.propGlowingMushroom, x: 1100, y: 1050, w: 100, h: 100 },
      { key: TEX.propGlowingMushroom, x: 450, y: 700, w: 100, h: 100 },
      { key: TEX.propGlowingMushroom, x: 1300, y: 650, w: 100, h: 100 },
      { key: TEX.propGlowingMushroom, x: 600, y: 300, w: 100, h: 100 },
      { key: TEX.propGlowingMushroom, x: 1200, y: 250, w: 100, h: 100 },
      // Elf lanterns
      { key: TEX.propElfLantern, x: 900, y: 1180, w: 100, h: 132 },
      { key: TEX.propElfLantern, x: 1050, y: 950, w: 100, h: 132 },
      { key: TEX.propElfLantern, x: 750, y: 600, w: 100, h: 132 },
      { key: TEX.propElfLantern, x: 1150, y: 400, w: 100, h: 132 },
      // Rune stones
      { key: TEX.propRuneStone, x: 640, y: 1000, w: 80, h: 134 },
      { key: TEX.propRuneStone, x: 1440, y: 750, w: 80, h: 134 },
      { key: TEX.propRuneStone, x: 480, y: 440, w: 80, h: 134 },
      { key: TEX.propRuneStone, x: 900, y: 150, w: 80, h: 134 },
    ];
    for (const p of NORIA_PROPS) {
      if (!scene.textures.exists(p.key)) continue;
      scene.add
        .image(p.x, p.y, p.key)
        .setDisplaySize(p.w, p.h)
        .setDepth(p.y);
    }

    // Pots/chests (only when no base map)
    if (!useBaseMap) {
      for (let i = 0; i < 6; i++) {
        const x = Math.floor(rng() * FIELD_W);
        const y = Math.floor(rng() * FIELD_H);
        scene.add
          .image(x * TILE, y * TILE, TEX.tinyThings, rng() < 0.5 ? TH.chestS : TH.potM)
          .setScale(TILE / 16)
          .setDepth(y * TILE);
      }
    }
  } else if (theme === "devias") {
    // Snow mountain: yellow grass (snow-tinted via image not available, use light variants),
    // path dirt, snow-mountain tile (#63), boulders
    r.fill(T.floorRedSquare); // light gray-ish stone-ish tile as snow base
    // snow patches with leavesAutumn (white-ish) — use voidBlack=22 sparingly to denote dark crags
    for (let i = 0; i < 30; i++) {
      const cx = Math.floor(rng() * FIELD_W);
      const cy = Math.floor(rng() * FIELD_H);
      r.rect(cx - 1, cy - 1, cx + 1, cy + 1, T.pathDirt);
    }
    // mountain ridge along top
    for (let x = 0; x < FIELD_W; x++) {
      if (rng() < 0.7) r.setTile(x, 0, T.snowMountain);
      if (rng() < 0.4) r.setTile(x, 1, T.snowMountain);
    }
    r.render();
    // boulders + ice torch placeholders
    for (let i = 0; i < 15; i++) {
      const x = Math.floor(rng() * FIELD_W);
      const y = Math.floor(rng() * FIELD_H);
      scene.add
        .image(x * TILE, y * TILE, TEX.tinyTiles, T.rockOrange)
        .setScale(TILE / 16)
        .setDepth(y * TILE);
    }
    for (let i = 0; i < 8; i++) {
      const x = Math.floor(rng() * FIELD_W);
      const y = Math.floor(rng() * FIELD_H);
      scene.add
        .image(x * TILE, y * TILE, TEX.tinyThings, TH.torchBlueLit)
        .setScale(TILE / 16)
        .setDepth(y * TILE);
    }
  } else if (theme === "atlans") {
    // Underwater: water tile + cobble paths as coral platforms
    r.fill(T.water);
    for (let i = 0; i < 12; i++) {
      const cx = Math.floor(rng() * FIELD_W);
      const cy = Math.floor(rng() * FIELD_H);
      const sz = 2 + Math.floor(rng() * 4);
      r.rect(cx - sz, cy - sz, cx + sz, cy + sz, T.pathCobble);
    }
    r.render();
    // Coral reef tinted bushes
    for (let i = 0; i < 25; i++) {
      const x = Math.floor(rng() * FIELD_W);
      const y = Math.floor(rng() * FIELD_H);
      scene.add
        .image(x * TILE, y * TILE, TEX.tinyTiles, T.bushGreen)
        .setScale(TILE / 16)
        .setTint(0xff8a80)
        .setDepth(y * TILE);
    }
  } else if (theme === "icarus") {
    // Sky islands: cobble platforms over a void, with bright clouds and torches
    r.fill(T.voidBlack);
    for (let i = 0; i < 18; i++) {
      const cx = Math.floor(rng() * FIELD_W);
      const cy = Math.floor(rng() * FIELD_H);
      const sz = 3 + Math.floor(rng() * 4);
      r.rect(cx - sz, cy - sz, cx + sz, cy + sz, T.pathCobble);
    }
    r.render();
    for (let i = 0; i < 20; i++) {
      const x = Math.floor(rng() * FIELD_W);
      const y = Math.floor(rng() * FIELD_H);
      scene.add
        .image(x * TILE, y * TILE, TEX.tinyTiles, T.fountainTop)
        .setScale(TILE / 16)
        .setTint(0xb0e0ff)
        .setAlpha(0.8)
        .setDepth(y * TILE);
    }
  } else if (theme === "kalima") {
    // Kalima dimension: red brick floor with dark shadow patches & purple tint
    r.fill(T.brickPathRed);
    for (let i = 0; i < 25; i++) {
      const cx = Math.floor(rng() * FIELD_W);
      const cy = Math.floor(rng() * FIELD_H);
      r.rect(cx - 1, cy - 1, cx + 1, cy + 1, T.voidBlack);
    }
    r.render();
    for (let i = 0; i < 12; i++) {
      const x = Math.floor(rng() * FIELD_W);
      const y = Math.floor(rng() * FIELD_H);
      scene.add
        .image(x * TILE, y * TILE, TEX.tinyThings, TH.torchOrangeLit)
        .setScale(TILE / 16)
        .setTint(0xb284ff)
        .setDepth(y * TILE);
    }
  } else if (theme === "crywolf") {
    // Crywolf fortress: stone floor + scattered grass + altar relics
    r.fill(T.floorRedSquare);
    for (let i = 0; i < 40; i++) {
      const x = Math.floor(rng() * FIELD_W);
      const y = Math.floor(rng() * FIELD_H);
      r.setTile(x, y, T.grassGreen);
    }
    r.render();
    for (let i = 0; i < 14; i++) {
      const x = Math.floor(rng() * FIELD_W);
      const y = Math.floor(rng() * FIELD_H);
      scene.add
        .image(x * TILE, y * TILE, TEX.tinyTiles, T.pillarFull)
        .setScale(TILE / 16)
        .setDepth(y * TILE);
    }
  } else {
    // tarkan
    // Desert: yellowGrass + leavesAutumn (sand) + cactus props
    r.fill(T.grassYellow);
    for (let i = 0; i < 60; i++) {
      const x = Math.floor(rng() * FIELD_W);
      const y = Math.floor(rng() * FIELD_H);
      r.setTile(x, y, T.leavesAutumn);
    }
    // dunes deco
    for (let i = 0; i < 100; i++) {
      const x = Math.floor(rng() * FIELD_W);
      const y = Math.floor(rng() * FIELD_H);
      r.setDeco(x, y, T.patchYellow);
    }
    r.render();
    // cactus + rocks
    for (let i = 0; i < 18; i++) {
      const x = Math.floor(rng() * FIELD_W);
      const y = Math.floor(rng() * FIELD_H);
      scene.add
        .image(
          x * TILE,
          y * TILE,
          TEX.tinyTiles,
          rng() < 0.6 ? T.treeMedium : T.rockOrange,
        )
        .setScale(TILE / 16)
        .setTint(0xc4a35e)
        .setDepth(y * TILE);
    }
  }
}
